import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CANLI_LISTE_SINIRI,
  CANLI_YENILEME_MS,
  type CanliOturumSatiri,
} from "@koclukpanel/contracts";
import { BosDurum, Buton, Iskelet, Ikon, Kart, NotKutusu, Rozet } from "@koclukpanel/ui";
import { SayfaBasligiEki } from "./Kabuk";
import { ApiHatasi, canliListe, canliModulAyarla, canliModulDurumu } from "./api";
import {
  CANLI_LISTE_ANAHTARI,
  CANLI_MODUL_ANAHTARI,
  SUPHELI_ETIKETI,
  baslangicYazisi,
  listeYenilemeAraligi,
  modulKapaliHatasi,
  satirKonusu,
  satirlariAyir,
  sonSinyalYazisi,
  sureYazisi,
} from "./canli-calisma";
import { hataMetni } from "./hata-metni";
import { useSudoKapisi } from "./SudoKapisi";

/**
 * CANLI ÇALIŞMA — "Şu an çalışanlar" (F-LIV-01..05)
 *
 * Bu ekran sunucunun cevabını gösterir; kapsam (kimin hangi öğrenciyi göreceği), modülün açık olup
 * olmadığı ve eşikler (sessiz, şüpheli) SUNUCUDA. Burada hiçbir şey süzülmüyor: süzseydik, uca doğrudan
 * istek atan biri farklı bir liste görürdü.
 *
 * ================================================================
 * MODÜL ANAHTARI AYNI SAYFADA — VE KAPALIYKEN DE ULAŞILABİLİR
 * ================================================================
 * Menü öğesi modül kapalıyken izleyicilerden gizlenir (F-LIV-05), ama anahtarı yönetebilen kişiden
 * GİZLENMEZ: anahtar bu sayfada olduğu için, kapatan kişi sayfayı da kaybederse modülü bir daha
 * açamazdı. Bu karar `Uygulama.tsx` içindeki `ekranGorunur`da; sunucu yine her uçta kendi iznini sorar.
 *
 * Websocket YOK: liste 30 saniyede bir sorgulanıyor (F-LIV-04).
 */
export function CanliCalisma({
  goruntule,
  yonetebilir,
  onBilgiAc,
  onOgrenciAc,
}: {
  /** `canli.izleme.goruntule` — yoksa liste hiç sorgulanmaz. */
  goruntule: boolean;
  /** `canli.izleme.yonet` — modül anahtarı yalnız buna görünür. */
  yonetebilir: boolean;
  /** "Bu ekran hakkında" — bilgilendirme sayfasına gider. */
  onBilgiAc?: () => void;
  /** Satırdan öğrencinin 360° sayfasına gider; verilmezse düğme çizilmez. */
  onOgrenciAc?: (ogrenciId: string, ad: string) => void;
}) {
  const modul = useQuery({
    queryKey: CANLI_MODUL_ANAHTARI,
    queryFn: () => canliModulDurumu(),
    retry: false,
    staleTime: 60_000,
  });
  // `data` yokken (okunamadı) "kapalı" SAYILMIYOR: durum bilinmiyorsa liste yine denenir, kapalıysa
  // sunucu 404 `MODUL_KAPALI` ile kendisi söyler.
  const kapali = modul.data?.acik === false;

  return (
    <div className="flex flex-col gap-4">
      <SayfaBasligiEki aciklama="Şu an çalışan öğrencileri anlık izleyin." />
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="text-notr-600 text-sm">
          Çalışma sayacı şu an açık olan öğrenciler. Liste 30 saniyede bir yenilenir.
        </p>
        {onBilgiAc && (
          <Buton gorunum="sessiz" onClick={onBilgiAc}>
            Bu ekran hakkında
          </Buton>
        )}
      </div>

      {yonetebilir && <ModulKarti acik={modul.data?.acik} bekliyor={modul.isPending} />}

      {modul.isPending ? (
        <Iskelet satir={3} />
      ) : kapali ? (
        <BosDurum baslik="Canlı izleme kapalı." />
      ) : goruntule ? (
        <ListeBolumu onOgrenciAc={onOgrenciAc} />
      ) : (
        <BosDurum
          baslik="Canlı çalışmayı görme yetkiniz yok"
          aciklama="Bu ekranda yalnız modül anahtarını yönetebilirsiniz."
        />
      )}
    </div>
  );
}

function ListeBolumu({ onOgrenciAc }: { onOgrenciAc?: (ogrenciId: string, ad: string) => void }) {
  const qc = useQueryClient();
  const liste = useQuery({
    queryKey: CANLI_LISTE_ANAHTARI,
    queryFn: () => canliListe(),
    retry: false,
    refetchInterval: (sorgu) => listeYenilemeAraligi(sorgu.state.error, CANLI_YENILEME_MS),
  });

  // Menü açıkken modül kapatıldıysa menü ve ekran kendiliğinden toparlansın: modül durumu yeniden okunur.
  const kapaliHatasi = modulKapaliHatasi(liste.error);
  useEffect(() => {
    if (kapaliHatasi) void qc.invalidateQueries({ queryKey: CANLI_MODUL_ANAHTARI });
  }, [kapaliHatasi, qc]);

  if (liste.isPending) return <Iskelet satir={4} />;

  if (liste.isError) {
    if (kapaliHatasi) return <BosDurum baslik="Canlı izleme kapalı." />;
    if (liste.error instanceof ApiHatasi && liste.error.durum === 403) {
      return (
        <BosDurum
          baslik="Canlı çalışmayı görme yetkiniz yok"
          aciklama="Yetki gerektiğini düşünüyorsanız yöneticinize söyleyin."
        />
      );
    }
    return (
      <BosDurum
        ton="hata"
        baslik="Liste alınamadı"
        aciklama={hataMetni(liste.error, "Bağlantınızı kontrol edin.")}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void liste.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  const { calisanlar, sessizler } = satirlariAyir(liste.data.satirlar);

  return (
    <div className="flex flex-col gap-4">
      {/* Sınıra takıldıysa listenin tamamı olmadığı söylenmeli; eksik liste "herkes bu" gibi okunur. */}
      {liste.data.kesildi && <NotKutusu>İlk {CANLI_LISTE_SINIRI} sayaç gösteriliyor.</NotKutusu>}

      {/*
        UNİKOÇ mini istatistik kartları (tarama-1 §3.3): solda ikonlu açık kare, sayı kalın 20 px
        + küçük etiket. UNİKOÇ'taki "Atanan öğrenci" ve "Aktif eşik" kartları burada YOK: bu uç
        o sayıları vermiyor; olmayan veri uydurulmadı.
      */}
      <dl className="grid grid-cols-2 gap-3">
        <div className="bg-notr-0 border-mor-kenar flex items-center gap-3 rounded-2xl border px-4 py-3 shadow-(--golge-unk-kart)">
          <span
            aria-hidden="true"
            className="bg-tamam-zemin text-tamam-metin grid size-10 shrink-0 place-items-center rounded-xl"
          >
            <Ikon ad="canli" boyut="normal" />
          </span>
          <div>
            <dd className="text-metin-koyu text-xl leading-none font-bold tabular-nums">
              {calisanlar.length}
            </dd>
            <dt className="text-metin-ikincil text-xs">Şu an aktif</dt>
          </div>
        </div>
        <div className="bg-notr-0 border-mor-kenar flex items-center gap-3 rounded-2xl border px-4 py-3 shadow-(--golge-unk-kart)">
          <span
            aria-hidden="true"
            className="bg-bekliyor-zemin text-bekliyor-metin grid size-10 shrink-0 place-items-center rounded-xl"
          >
            <Ikon ad="bekliyor" boyut="normal" />
          </span>
          <div>
            <dd className="text-metin-koyu text-xl leading-none font-bold tabular-nums">
              {sessizler.length}
            </dd>
            <dt className="text-metin-ikincil text-xs">Bağlantısı kopan</dt>
          </div>
        </div>
      </dl>

      {calisanlar.length === 0 && sessizler.length === 0 ? (
        <BosDurum baslik="Şu an çalışan öğrenci yok." />
      ) : (
        <>
          {calisanlar.length === 0 ? (
            <p className="text-notr-600 text-sm">Şu an çalışan öğrenci yok.</p>
          ) : (
            <Kart baslik="Şu an çalışanlar">
              <ul className="divide-notr-200 flex flex-col divide-y">
                {calisanlar.map((s) => (
                  <CalisanSatiri
                    key={s.oturumId}
                    satir={s}
                    an={liste.data.an}
                    onOgrenciAc={onOgrenciAc}
                  />
                ))}
              </ul>
            </Kart>
          )}
          {sessizler.length > 0 && (
            <SessizBolum satirlar={sessizler} an={liste.data.an} onOgrenciAc={onOgrenciAc} />
          )}
        </>
      )}
    </div>
  );
}

function SatirBasligi({ satir }: { satir: CanliOturumSatiri }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="text-notr-900 text-sm font-medium">{satir.kisaAd}</p>
      <p className="text-notr-600 text-sm">
        {satir.ders} · {satirKonusu(satir)}
      </p>
    </div>
  );
}

function OgrenciDugmesi({
  satir,
  onOgrenciAc,
}: {
  satir: CanliOturumSatiri;
  onOgrenciAc?: (ogrenciId: string, ad: string) => void;
}) {
  if (!onOgrenciAc) return null;
  return (
    <Buton
      gorunum="sessiz"
      aria-label={`${satir.kisaAd} — öğrencinin sayfasını aç`}
      onClick={() => onOgrenciAc(satir.ogrenciId, satir.kisaAd)}
    >
      Öğrenciyi aç
    </Buton>
  );
}

/**
 * Şüpheli rozeti ÇALIŞAN ve SESSİZ satırda aynı bileşen/metin (S8-KOD-06): 7 saat önce başlamış, son
 * sinyali 10 dk önce olan sayaç SESSİZ bölümüne düşer ama şüphelidir; rozet yalnız çalışan satırda
 * olsaydı o uyarı sessizce kaybolurdu. Renk tek başına anlam taşımaz: işaret ve metin birlikte.
 * Otomatik kapatma YOK (F-LIV-03).
 */
function SupheliRozeti({ satir }: { satir: CanliOturumSatiri }) {
  if (!satir.supheli) return null;
  return (
    <Rozet ton="uyari" isaret="!">
      {SUPHELI_ETIKETI}
    </Rozet>
  );
}

/** Sayacın başladığı an (S8-KOD-05); satır dar ekranda alta sarılsın diye kendi satırında kırılabilir. */
function BaslangicYazisi({ satir, an }: { satir: CanliOturumSatiri; an: string }) {
  const yazi = baslangicYazisi(an, satir.baslangic);
  if (!yazi) return null;
  return <span className="text-notr-600 text-sm tabular-nums">{yazi}</span>;
}

function CalisanSatiri({
  satir,
  an,
  onOgrenciAc,
}: {
  satir: CanliOturumSatiri;
  an: string;
  onOgrenciAc?: (ogrenciId: string, ad: string) => void;
}) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 first:pt-0 last:pb-0">
      <SatirBasligi satir={satir} />
      <span className="text-notr-900 text-sm tabular-nums">{sureYazisi(satir.sureSaniye)}</span>
      <BaslangicYazisi satir={satir} an={an} />
      <SupheliRozeti satir={satir} />
      <OgrenciDugmesi satir={satir} onOgrenciAc={onOgrenciAc} />
    </li>
  );
}

/**
 * Bağlantısı kopan sayaçlar (F-LIV-03). Varsayılan KAPALI: uygulaması kapanmış öğrenci "çalışıyor"
 * değildir ve ana listeyi kalabalıklaştırmamalı; ama var olduğu da gizlenmemeli (sayı düğmede yazılı).
 */
function SessizBolum({
  satirlar,
  an,
  onOgrenciAc,
}: {
  satirlar: CanliOturumSatiri[];
  an: string;
  onOgrenciAc?: (ogrenciId: string, ad: string) => void;
}) {
  const [acik, setAcik] = useState(false);
  return (
    <Kart>
      <button
        type="button"
        aria-expanded={acik}
        aria-controls="canli-sessiz-liste"
        onClick={() => setAcik((a) => !a)}
        className="min-h-dokunma text-notr-800 flex w-full items-center justify-between gap-2 text-left text-sm font-semibold"
      >
        <span>Bağlantısı kopan sayaçlar ({satirlar.length})</span>
        <span aria-hidden="true">{acik ? "▴" : "▾"}</span>
      </button>
      {acik && (
        <ul id="canli-sessiz-liste" className="divide-notr-200 mt-2 flex flex-col divide-y">
          {satirlar.map((s) => (
            <li
              key={s.oturumId}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 first:pt-0 last:pb-0"
            >
              <SatirBasligi satir={s} />
              <BaslangicYazisi satir={s} an={an} />
              <span className="text-notr-600 text-sm">
                Son sinyal: {sonSinyalYazisi(an, s.sonSinyal)}
              </span>
              <SupheliRozeti satir={s} />
              <OgrenciDugmesi satir={s} onOgrenciAc={onOgrenciAc} />
            </li>
          ))}
        </ul>
      )}
    </Kart>
  );
}

/**
 * Modül anahtarı (F-LIV-05). Aç/kapat sudo ister: sunucu `SUDO_GEREKLI` dönünce şifre sorulur, şifre
 * doğrulanınca işlem kaldığı yerden tekrar denenir. Sonuç ekranda bildirilir.
 */
function ModulKarti({ acik, bekliyor }: { acik: boolean | undefined; bekliyor: boolean }) {
  const qc = useQueryClient();
  const [sonuc, setSonuc] = useState<string | null>(null);
  const { hataYakala, kapi } = useSudoKapisi(
    "Canlı izleme modülünü değiştirmek için şifrenizi yeniden girin.",
  );
  const mut = useMutation({
    mutationFn: (yeniDurum: boolean) => canliModulAyarla(yeniDurum),
    onMutate: () => setSonuc(null),
    onSuccess: (yanit) => {
      setSonuc(yanit.acik ? "Canlı izleme açıldı." : "Canlı izleme kapatıldı.");
      void qc.invalidateQueries({ queryKey: CANLI_MODUL_ANAHTARI });
      void qc.invalidateQueries({ queryKey: CANLI_LISTE_ANAHTARI });
    },
    // `yeniDurum`: yeniden deneme AYNI hedef değerle yapılıyor; sudo penceresi açıkken ekrandaki durum
    // değişmiş olabilir ve ters yöne çevirmek istenmeyen bir açma/kapama olurdu.
    onError: (hata, yeniDurum) => hataYakala(() => mut.mutate(yeniDurum))(hata),
  });
  const sudoBekliyor = mut.error instanceof ApiHatasi && mut.error.sudoGerekliMi;

  return (
    <Kart
      baslik="Modül"
      aciklama="Kapalıyken liste, menü öğesi ve öğrencinin gördüğü not görünmez."
    >
      {bekliyor ? (
        <Iskelet satir={1} />
      ) : acik === undefined ? (
        <p role="alert" className="text-hata-700 text-sm">
          Modül durumu alınamadı.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-notr-900 font-medium">{acik ? "Açık" : "Kapalı"}</span>
          <Buton gorunum="ikincil" disabled={mut.isPending} onClick={() => mut.mutate(!acik)}>
            {acik ? "Kapat" : "Aç"}
          </Buton>
        </div>
      )}
      {sonuc && (
        <p role="status" className="text-notr-700 mt-2 text-sm">
          {sonuc}
        </p>
      )}
      {mut.isError && !sudoBekliyor && (
        <p role="alert" className="text-hata-700 mt-2 text-sm">
          {hataMetni(mut.error, "Modül durumu değiştirilemedi.")}
        </p>
      )}
      {kapi}
    </Kart>
  );
}
